import i18n from "i18next";
import HttpApi from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

import LanguageDetector from "i18next-browser-languagedetector";
import { VITE_REACT_APP_PUBLIC_S3 } from "../constants/variables";
import {
  DEFAULT_APP_LANGUAGE_CODE,
  PICKABLE_APP_LANGUAGE_CODES,
  SUPPORTED_APP_LANGUAGE_CODES,
} from "../constants/appLanguages";

const base = `${VITE_REACT_APP_PUBLIC_S3}/locales/locale`;
const path = `${base}/{{lng}}.json?date=${Date.now()}`;

const PICKABLE_BROWSER_LANGUAGE = "pickableBrowserLanguage";

// The browser language only chooses among pickable languages: a new user must
// not land on a language the pickers no longer offer (same rule as mela-app).
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: PICKABLE_BROWSER_LANGUAGE,
  lookup: () =>
    (navigator.languages?.length ? navigator.languages : [navigator.language])
      .map((tag) => (tag ?? "").toLowerCase().split("-")[0])
      .filter((code) => PICKABLE_APP_LANGUAGE_CODES.includes(code)),
});

// A missing key renders its defaultValue or the raw key without any error.
// Report each one once per page load, capped so a broken screen cannot flood
// the network transport. i18next only calls this when the fallback language
// lacks the key too; a merely untranslated key is not reported.
const MAX_REPORTED_MISSING_KEYS = 50;
const reportedMissingKeys = new Set<string>();

function reportMissingKey(
  _languages: readonly string[],
  _namespace: string,
  key: string,
  fallbackValue: string,
) {
  // Some call sites pass arbitrary text through t(); only real catalogue keys
  // are worth a report. Before the catalogue arrives every key looks missing.
  if (
    !key.endsWith(".translation") ||
    !i18n.hasLoadedNamespace("translation") ||
    reportedMissingKeys.has(key) ||
    reportedMissingKeys.size >= MAX_REPORTED_MISSING_KEYS
  ) {
    return;
  }
  // The anonymous badge page must never load the logger chain (see index.tsx).
  if (window.location.pathname.startsWith("/badge/")) {
    return;
  }
  reportedMissingKeys.add(key);
  void import("@/utils/logs/LoggerInstance").then(({ default: logger }) =>
    logger.warn("Missing translation key", ["locales", "missing-key"], {
      key,
      language: i18n.language,
      hasDefaultValue: fallbackValue !== key,
    }),
  );
}

// Date localization is handled by date-fns per call (see src/locales/dateFnsLocale.ts).
i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(languageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .use(HttpApi)
  .init({
    backend: { loadPath: path },
    // German and Russian were dropped: a saved or profile choice of them
    // resolves to the fallback language.
    supportedLngs: SUPPORTED_APP_LANGUAGE_CODES,
    fallbackLng: DEFAULT_APP_LANGUAGE_CODE,
    load: "languageOnly",
    detection: {
      order: ["querystring", "localStorage", PICKABLE_BROWSER_LANGUAGE],
    },
    // missingKeyHandler replaces the backend's create(), so nothing is
    // posted to S3.
    saveMissing: true,
    missingKeyHandler: reportMissingKey,

    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  });

export default i18n;
